import { apiService } from "../../../utils/api/api.service";
import { requestWithErrorHandling } from "../../../utils/error/handle.error";
import {
  ListConversationsApiResponse,
  SendMessageData,
} from "../interfaces/chat.interface";

class ChatService {
  private static instance: ChatService;

  static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  // Get all conversations (paginated)
  getConversations = (page: number = 1, limit: number = 15) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<ListConversationsApiResponse>(
        "/chat/conversations",
        {
          params: { page, limit },
        },
      );
    });
  };

  // Get specific conversation with messages
  getConversation = (
    conversationId: string,
    params?: { page?: number; limit?: number },
  ) => {
    return requestWithErrorHandling(async () => {
      // Changed from /messages/conversations/${conversationId} to match Messages.tsx
      return await apiService.get<any>(
        `/chat/conversations/${conversationId}/messages`,
        { params },
      );
    });
  };

  // Get conversation metadata
  getConversationMetadata = (conversationId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<any>(`/chat/conversations/${conversationId}`);
    });
  };

  // Send message
  sendMessage = (data: SendMessageData) => {
    return requestWithErrorHandling(async () => {
      // Endpoint might need verification, keeping as is or matching Messages.tsx if found
      return await apiService.post<any>("/chat/send", data);
    });
  };

  // Mark single message as read
  markMessageAsRead = (messageId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.put<any>(`/chat/messages/${messageId}/read`, {});
    });
  };

  // Mark entire conversation as read
  markConversationAsRead = (conversationId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.put<any>(
        `/chat/conversations/${conversationId}/read`,
        {},
      );
    });
  };

  // Create or get existing conversation with a participant (e.g. seller)
  createConversation2 = (participantId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<{
        success: boolean;
        data?: { id: string };
      }>("/chat/conversations", { participantId });
    });
  };

  // Contact request (start conversation from request)'
  contactRequest = (data: {
    requestId: string;
    message: string;
    productData: any;
  }) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>("/chat/contact-request", data);
    });
  };

  // Get conversations for a product (owner view)
  getConversationsByProduct = (productId: string, page: number = 1, limit: number = 15) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<any>(
        `/chat/conversations/product/${productId}`,
        {
          params: { page, limit },
        },
      );
    });
  };

  // Create or get conversation (contact seller from product detail)
  createConversation = (data: {
    participantId: string;
    productId: string;
    productData: any;
  }) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>("/chat/conversations", data);
    });
  };

  // Contact seller (simpler endpoint - creates conversation + first message)
  contactSeller = async (data: { productId: string; initialMessage?: string }) => {
    const response = await apiService.post<any>("/chat/contact-seller", data);
    const payload = response?.data;

    if (!payload?.success) {
      throw new Error(payload?.message || "Failed to contact seller");
    }

    return payload?.data?.conversation ?? payload?.conversation ?? payload?.data;
  };
}

export const chatService = ChatService.getInstance();
